import type { Task } from '@lmc/ts-common'
import { Edit } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { getJSON } from 'common/services/api'
import { useAbilityAccess } from 'components/AccessControl'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../../../../actions'

type EditRecurrenceButtonProps = {
  recurringTaskID: string
}

const EditRecurrenceButton = ({
  recurringTaskID
}: EditRecurrenceButtonProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const navigate = useNavigate()

  const dispatch = useDispatch()
  const { isLoading, refetch } = useQuery<{ result: Task }>(
    'recurringTask',
    () =>
      getJSON({
        url: `/api/list/recurring-tasks/${recurringTaskID}`
      }),
    {
      enabled: false
    }
  )
  const onEditComplete = () => {
    navigate('/todos/dashboard')
    refetch()
  }

  const openModal = (task: Task) => {
    navigate('/todos/manage')
    const chosenList = task?.assignee?.length ? 'assignee' : 'resident'

    const idList = (task[chosenList] as string[]).join(',') || []

    const formTask = { ...task, [chosenList]: idList }
    dispatch(
      Actions.openModal({
        modalId: 'editTask',
        data: {
          onComplete: onEditComplete
        }
      })
    )
    dispatch(Actions.addForm({ formId: 'editTask', initial: formTask }))
  }

  const hasRolePermission = useAbilityAccess([
    {
      i: 'update',
      this: 'Task'
    }
  ])

  return (
    <Wrapper>
      {!isLoading && hasRolePermission && (
        <EditButton
          variant="text"
          icon={<Edit />}
          isLoading={isLoading}
          onClick={async () => {
            await refetch()?.then((data) => {
              openModal(data?.data?.result as Task)
            })
          }}
        >
          <Text level={1}>{t(toDosConstants.EDIT_RECURRENCE)}</Text>
        </EditButton>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
  padding-top: 32px;
`

const EditButton = styled(Button)`
  color: ${({ theme }) => theme.color.neutral.color4};
  svg path {
    stroke: ${({ theme }) => theme.color.neutral.color4};
  }
  &:hover {
    svg path {
      stroke: ${({ theme }) => theme.color.status.primary};
    }
  }
`

export default EditRecurrenceButton
